import {
  ProjectParticipationsDataT,
  ProjectParticipationsT,
} from '@/components/common/menu/lib/types/navigationDataTypes.ts'
import { api } from '@/lib/api-client.ts'
import { useInfiniteQuery } from '@tanstack/react-query'

export type GetProjectsParticipationsReturnT = {
  projectParticipations: ProjectParticipationsT
  userParticipationsCount: number
  nextPage: number | null
  currentPage: number
}

export async function getUserProjectParticipations(
  page: number,
): Promise<GetProjectsParticipationsReturnT | undefined> {
  // get project stages accessible for the currently logged user
  const response = await api.get(`/users/me/project_participations`)
  const data: ProjectParticipationsDataT = response.data
  const projectParticipations: ProjectParticipationsT = data[`hydra:member`] || []
  const userParticipationsCount: number = response.data[`hydra:totalItems`] ?? 0
  const hasNextPage =
    page * projectParticipations?.length < userParticipationsCount &&
    projectParticipations?.length > 0
  return {
    projectParticipations,
    userParticipationsCount,
    nextPage: hasNextPage ? page + 1 : null,
    currentPage: page,
  }
}

export const userProjectParticipationsQuery = () => ({
  queryKey: ['userProjectParticipations'],
  queryFn: ({ pageParam = 1 }) => getUserProjectParticipations(pageParam),
  getNextPageParam: (lastPage: unknown) => (lastPage as { nextPage: number | null })?.nextPage,
  initialPageParam: 1,
  staleTime: 1000 * 60,
})

export function useGetUserProjectParticipations() {
  const {
    data: userProjectParticipations,
    hasNextPage: userProjectsHasNextPage,
    isFetchingNextPage: userProjectsIsFetchingNextPage,
    fetchNextPage: userProjectsFetchNextPage,
  } = useInfiniteQuery({ ...userProjectParticipationsQuery() })

  const projectParticipationsFlat =
    userProjectParticipations?.pages.flatMap(page => page?.projectParticipations ?? []) ?? []
  //

  return {
    projectParticipationsFlat,
    userProjectsHasNextPage,
    userProjectsIsFetchingNextPage,
    userProjectsFetchNextPage,
    hasParticipations: projectParticipationsFlat.length > 0,
  }
}
